import React, { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import AuthenticatedLayout from '../../components/layouts/authenticated_layout'
import LoadingSpinner from '../../components/loading/loading_spinner'
import AiLoadingOverlay from '../../components/loading/ai_loading_overlay'
import DeleteConfirmModal from '../../components/modals/delete_confirm_modal'
import Breadcrumb from '../../components/common/breadcrumb'
import { PdfOrientationModal } from './components/pdf_orientation_modal'
import { useBusinessAbstractDetail } from './hooks/business_abstract/use_business_abstract_detail'
import { useBusinessAbstractUpdate } from './hooks/business_abstract/use_business_abstract_update'
import { useBusinessAbstractRegenerate } from './hooks/business_abstract/use_business_abstract_regenerate'
import { BusinessAbstractFormData } from './hooks/business_abstract/types'
import { AiInstructionFormData } from './hooks/common/types'
import { useThreeCDetail } from './hooks/threec/use_threec_detail'
import { useThreeCUpdate } from './hooks/threec/use_threec_update'
import { useThreeCRegenerate } from './hooks/threec/use_threec_regenerate'
import { ThreeCFormData } from './hooks/threec/types'
import { useInvestmentPlanDetail } from './hooks/investment_plan/use_investment_plan_detail'
import { useInvestmentPlanUpdate } from './hooks/investment_plan/use_investment_plan_update'
import { useInvestmentPlanRegenerate } from './hooks/investment_plan/use_investment_plan_regenerate'
import {
  InvestmentPlanApiData,
  InvestmentPlanFormData,
} from './hooks/investment_plan/types'
import { useExecutionPlanDetail } from './hooks/execution_plan/use_execution_plan_detail'
import { useExecutionPlanUpdate } from './hooks/execution_plan/use_execution_plan_update'
import { useExecutionPlanRegenerate } from './hooks/execution_plan/use_execution_plan_regenerate'
import { ExecutionPlanFormData } from './hooks/execution_plan/types'
import { useFaqDetail } from './hooks/faq/use_faq_detail'
import { useFaqUpdate } from './hooks/faq/use_faq_update'
import { useFaqRegenerate } from './hooks/faq/use_faq_regenerate'
import { FaqFormData } from './hooks/faq/types'
import { usePartnersDetail } from './hooks/partners/use_partners_detail'
import { usePartnersUpdate } from './hooks/partners/use_partners_update'
import { usePartnersRegenerate } from './hooks/partners/use_partners_regenerate'
import { PartnersFormData } from './hooks/partners/types'
import FlatButton from '../../components/buttons/flat_button'
import EvaluationButton from '../../components/buttons/evaluation_button'
import { BusinessAbstractSlide } from './slides/business_abstract'
import { ThreeCSlide } from './slides/threec'
import { InvestmentPlanSlide } from './slides/investment_plan'
import { ExecutionPlanSlide } from './slides/execution_plan'
import { FaqSlide } from './slides/faq'
import { PartnersSlide } from './slides/partners'
import { useSnackBar } from '../../providers/snack_bar_provider'
import { useBusinessDelete } from './hooks/use_business_delete'
import { useBusinessIdeaUserEvaluation } from '../../hooks/use_business_idea_user_evaluation'
import {
  PdfOrientation,
  useBusinessPdfDownload,
} from './hooks/use_business_pdf_download'

const BusinessDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [isEditing, setIsEditing] = useState(false) // 編集モードかどうか
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false) // 削除確認モーダルの表示状態
  const [isPdfModalOpen, setIsPdfModalOpen] = useState(false) // PDF向き選択モーダルの表示状態
  const { showSnackBar } = useSnackBar()
  const { isDeleting, deleteBusinessIdea } = useBusinessDelete()
  const { isDownloading, downloadPdf } = useBusinessPdfDownload()

  // 各スライドへのref
  const businessAbstractSlideRef = useRef<HTMLDivElement>(null)
  const threeCSlideRef = useRef<HTMLDivElement>(null)
  const investmentPlanSlideRef = useRef<HTMLDivElement>(null)
  const executionPlanSlideRef = useRef<HTMLDivElement>(null)
  const faqSlideRef = useRef<HTMLDivElement>(null)
  const partnersSlideRef = useRef<HTMLDivElement>(null)

  // 事業概要
  const {
    businessAbstract,
    setBusinessAbstract,
    userEvaluation,
    isBusinessAbstractLoading,
  } = useBusinessAbstractDetail(id || '')

  // 評価機能
  const { currentEvaluation, isEvaluating, evaluate } =
    useBusinessIdeaUserEvaluation(id || '', userEvaluation)
  const { isUpdating: isAbstractUpdating, updateBusinessAbstract } =
    useBusinessAbstractUpdate()
  const { isRegenerating: isAbstractRegenerating, regenerateBusinessAbstract } =
    useBusinessAbstractRegenerate()
  // 3C分析
  const { threeC, setThreeC, isThreeCLoading } = useThreeCDetail(id || '')
  const { isUpdating: isThreeCUpdating, updateThreeC } = useThreeCUpdate()
  const { isRegenerating: isThreeCRegenerating, regenerateThreeC } =
    useThreeCRegenerate()
  // 投資計画
  const { investmentPlan, setInvestmentPlan, isInvestmentPlanLoading } =
    useInvestmentPlanDetail(id || '')
  const { isUpdating: isInvestmentPlanUpdating, updateInvestmentPlan } =
    useInvestmentPlanUpdate()
  const {
    isRegenerating: isInvestmentPlanRegenerating,
    regenerateInvestmentPlan,
  } = useInvestmentPlanRegenerate()
  // 実行計画&必要なリソース
  const {
    slideExecutionPlan,
    setSlideExecutionPlan,
    isSlideExecutionPlanLoading,
  } = useExecutionPlanDetail(id || '')
  const { isUpdating: isSlideExecutionPlanUpdating, updateExecutionPlan } =
    useExecutionPlanUpdate()
  const {
    isRegenerating: isSlideExecutionPlanRegenerating,
    regenerateExecutionPlan,
  } = useExecutionPlanRegenerate()
  // FAQ・懸念点まとめ
  const { slideFaq, setSlideFaq, isSlideFaqLoading } = useFaqDetail(id || '')
  const { isUpdating: isSlideFaqUpdating, updateFaq } = useFaqUpdate()
  const { isRegenerating: isSlideFaqRegenerating, regenerateFaq } =
    useFaqRegenerate()
  // オススメ共創パートナー企業
  const { slidePartners, setSlidePartners, isSlidePartnersLoading } =
    usePartnersDetail(id || '')
  const { isUpdating: isSlidePartnersUpdating, updatePartners } =
    usePartnersUpdate()
  const { isRegenerating: isSlidePartnersRegenerating, regeneratePartners } =
    usePartnersRegenerate()

  // 更新中かどうか
  const isUpdating =
    isAbstractUpdating ||
    isThreeCUpdating ||
    isInvestmentPlanUpdating ||
    isSlideExecutionPlanUpdating ||
    isSlideFaqUpdating ||
    isSlidePartnersUpdating

  // AI再生成中かどうか
  const isRegenerating =
    isAbstractRegenerating ||
    isThreeCRegenerating ||
    isInvestmentPlanRegenerating ||
    isSlideExecutionPlanRegenerating ||
    isSlideFaqRegenerating ||
    isSlidePartnersRegenerating

  // 事業概要フォームの設定
  const {
    register: registerBusinessAbstract,
    handleSubmit: handleSubmitBusinessAbstract,
    reset: resetBusinessAbstract,
    setValue: setValueBusinessAbstract,
    control: controlBusinessAbstract,
    formState: businessAbstractFormState,
  } = useForm<BusinessAbstractFormData>({
    defaultValues: {
      projectName: '',
      thumbnailUrl: null,
      marketBackground: '',
      competitorAnalysis: '',
      businessChance: '',
      businessContentFirst: '',
      businessContentSecond: '',
    },
  })

  // 事業概要のAI指示フォームの設定
  const {
    register: registerBusinessAbstractAi,
    handleSubmit: handleSubmitBusinessAbstractAi,
    reset: resetBusinessAbstractAi,
    watch: watchBusinessAbstractAi,
  } = useForm<AiInstructionFormData>({
    defaultValues: {
      aiInstruction: '',
    },
  })

  // 3C分析フォームの設定
  const {
    register: registerThreeC,
    handleSubmit: handleSubmitThreeC,
    reset: resetThreeC,
    formState: threeCFormState,
  } = useForm<ThreeCFormData>({
    defaultValues: {
      needs: '',
      targetFirst: '',
      targetSecond: '',
      marketTrendFirst: '',
      marketTrendSecond: '',
      mainCompetitorFirst: '',
      mainCompetitorSecond: '',
      advantageFirst: '',
      advantageSecond: '',
      differentiationFirst: '',
      differentiationSecond: '',
      strengthFirst: '',
      strengthSecond: '',
      strengthThird: '',
      resourceFirst: '',
      resourceSecond: '',
      strategyFirst: '',
      strategySecond: '',
      strategyThird: '',
    },
  })

  // 3C分析のAI指示フォームの設定
  const {
    register: registerThreeCAi,
    handleSubmit: handleSubmitThreeCAi,
    reset: resetThreeCAi,
    watch: watchThreeCAi,
  } = useForm<AiInstructionFormData>({
    defaultValues: {
      aiInstruction: '',
    },
  })

  // 投資計画フォームの設定
  const {
    register: registerInvestmentPlan,
    handleSubmit: handleSubmitInvestmentPlan,
    reset: resetInvestmentPlan,
    control: controlInvestmentPlan,
    formState: investmentPlanFormState,
  } = useForm<InvestmentPlanFormData>({
    defaultValues: {
      profitStructure: '',
      profitStructureItems: [],
      cashFlow: '',
      cashFlowItems: [],
      financialPlan: '',
      financialPlanItems: [],
    },
  })

  // 投資計画のAI指示フォームの設定
  const {
    register: registerInvestmentPlanAi,
    handleSubmit: handleSubmitInvestmentPlanAi,
    reset: resetInvestmentPlanAi,
    watch: watchInvestmentPlanAi,
  } = useForm<AiInstructionFormData>({
    defaultValues: {
      aiInstruction: '',
    },
  })

  const businessAbstractAiInstruction = watchBusinessAbstractAi('aiInstruction')
  const threeCAiInstruction = watchThreeCAi('aiInstruction')
  const investmentPlanAiInstruction = watchInvestmentPlanAi('aiInstruction')

  // 実行計画&必要なリソースフォームの設定
  const {
    register: registerSlideExecutionPlan,
    handleSubmit: handleSubmitSlideExecutionPlan,
    reset: resetSlideExecutionPlan,
    control: controlSlideExecutionPlan,
    setValue: setSlideExecutionPlanValue,
    formState: slideExecutionPlanFormState,
  } = useForm<ExecutionPlanFormData>({
    defaultValues: {
      resource: '',
      maxPeriod: 24,
      scheduleItems: [],
    },
  })

  // 実行計画&必要なリソースAI再生成フォーム
  const {
    register: registerSlideExecutionPlanAi,
    handleSubmit: handleSubmitSlideExecutionPlanAi,
    reset: resetSlideExecutionPlanAi,
    watch: watchSlideExecutionPlanAi,
  } = useForm<AiInstructionFormData>({
    defaultValues: {
      aiInstruction: '',
    },
  })

  const slideExecutionPlanAiInstruction =
    watchSlideExecutionPlanAi('aiInstruction')

  // FAQ・懸念点まとめフォームの設定
  const {
    register: registerSlideFaq,
    handleSubmit: handleSubmitSlideFaq,
    reset: resetSlideFaq,
    control: controlSlideFaq,
    formState: slideFaqFormState,
  } = useForm<FaqFormData>({
    defaultValues: {
      concerns: [],
      faqs: [],
    },
  })

  // FAQ・懸念点まとめAI再生成フォーム
  const {
    register: registerSlideFaqAi,
    handleSubmit: handleSubmitSlideFaqAi,
    reset: resetSlideFaqAi,
    watch: watchSlideFaqAi,
  } = useForm<AiInstructionFormData>({
    defaultValues: {
      aiInstruction: '',
    },
  })

  const slideFaqAiInstruction = watchSlideFaqAi('aiInstruction')

  // オススメ共創パートナー企業フォームの設定
  const {
    register: registerSlidePartners,
    handleSubmit: handleSubmitSlidePartners,
    reset: resetSlidePartners,
    control: controlSlidePartners,
    setValue: setValueSlidePartners,
    formState: slidePartnersFormState,
  } = useForm<PartnersFormData>({
    defaultValues: {
      partners: [],
    },
  })

  // オススメ共創パートナー企業AI再生成フォーム
  const {
    register: registerSlidePartnersAi,
    handleSubmit: handleSubmitSlidePartnersAi,
    reset: resetSlidePartnersAi,
    watch: watchSlidePartnersAi,
  } = useForm<AiInstructionFormData>({
    defaultValues: {
      aiInstruction: '',
    },
  })

  const slidePartnersAiInstruction = watchSlidePartnersAi('aiInstruction')

  // 事業概要データの初期化
  useEffect(() => {
    if (businessAbstract) {
      resetBusinessAbstract({
        projectName: businessAbstract.projectName,
        thumbnailUrl: businessAbstract.thumbnailUrl,
        marketBackground: businessAbstract.sections.marketBackground,
        competitorAnalysis: businessAbstract.sections.competitorAnalysis,
        businessChance: businessAbstract.sections.businessChance,
        businessContentFirst: businessAbstract.sections.businessContentFirst,
        businessContentSecond: businessAbstract.sections.businessContentSecond,
      })
    }
  }, [businessAbstract, resetBusinessAbstract])

  // 3C分析データの初期化
  useEffect(() => {
    if (threeC) {
      resetThreeC({
        needs: threeC.customer.needs,
        targetFirst: threeC.customer.targetFirst,
        targetSecond: threeC.customer.targetSecond,
        marketTrendFirst: threeC.customer.marketTrendFirst,
        marketTrendSecond: threeC.customer.marketTrendSecond,
        mainCompetitorFirst: threeC.competitor.mainCompetitorFirst,
        mainCompetitorSecond: threeC.competitor.mainCompetitorSecond,
        advantageFirst: threeC.competitor.advantageFirst,
        advantageSecond: threeC.competitor.advantageSecond,
        differentiationFirst: threeC.competitor.differentiationFirst,
        differentiationSecond: threeC.competitor.differentiationSecond,
        strengthFirst: threeC.company.strengthFirst,
        strengthSecond: threeC.company.strengthSecond,
        strengthThird: threeC.company.strengthThird,
        resourceFirst: threeC.company.resourceFirst,
        resourceSecond: threeC.company.resourceSecond,
        strategyFirst: threeC.company.strategyFirst,
        strategySecond: threeC.company.strategySecond,
        strategyThird: threeC.company.strategyThird,
      })
    }
  }, [threeC, resetThreeC])

  // 収益モデルと投資計画データの初期化
  useEffect(() => {
    if (investmentPlan) {
      try {
        resetInvestmentPlan({
          profitStructure: investmentPlan.profitStructure,
          profitStructureItems: JSON.parse(
            investmentPlan.profitStructureItems || '[]'
          ),
          cashFlow: investmentPlan.cashFlow,
          cashFlowItems: JSON.parse(investmentPlan.cashFlowItems || '[]'),
          financialPlan: investmentPlan.financialPlan,
          financialPlanItems: JSON.parse(
            investmentPlan.financialPlanItems || '[]'
          ),
        })
      } catch (e) {
        console.error('収益モデルと投資計画データのパースに失敗しました:', e)
        showSnackBar(
          ['収益モデルと投資計画の読み込みに失敗しました。'],
          'error'
        )
      }
    }
  }, [investmentPlan, resetInvestmentPlan, showSnackBar])

  // 実行計画&必要なリソースデータの初期化
  useEffect(() => {
    if (slideExecutionPlan) {
      try {
        resetSlideExecutionPlan({
          resource: slideExecutionPlan.resource,
          maxPeriod: slideExecutionPlan.maxPeriod,
          scheduleItems: JSON.parse(slideExecutionPlan.scheduleItems || '[]'),
        })
      } catch (e) {
        console.error('実行計画&必要なリソースデータのパースに失敗しました:', e)
        showSnackBar(
          ['実行計画&必要なリソースの読み込みに失敗しました。'],
          'error'
        )
      }
    }
  }, [slideExecutionPlan, resetSlideExecutionPlan, showSnackBar])

  // FAQ・懸念点まとめデータの初期化
  useEffect(() => {
    if (slideFaq) {
      try {
        resetSlideFaq({
          concerns: JSON.parse(slideFaq.concerns || '[]'),
          faqs: JSON.parse(slideFaq.faqs || '[]'),
        })
      } catch (e) {
        console.error('FAQ・懸念点まとめデータのパースに失敗しました:', e)
        showSnackBar(['FAQ・懸念点まとめの読み込みに失敗しました。'], 'error')
      }
    }
  }, [slideFaq, resetSlideFaq, showSnackBar])

  // オススメ共創パートナー企業データの初期化
  useEffect(() => {
    if (slidePartners) {
      try {
        resetSlidePartners({
          partners: JSON.parse(slidePartners.partners || '[]'),
        })
      } catch (e) {
        console.error(
          'オススメ共創パートナー企業データのパースに失敗しました:',
          e
        )
        showSnackBar(
          ['オススメ共創パートナー企業の読み込みに失敗しました。'],
          'error'
        )
      }
    }
  }, [slidePartners, resetSlidePartners, showSnackBar])

  // 編集モードの開始
  const handleStartEdit = () => {
    setIsEditing(true)
  }

  // 削除ボタンクリック処理
  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true)
  }

  // PDFダウンロードボタンクリック処理（モーダルを開く）
  const handlePdfDownload = () => {
    setIsPdfModalOpen(true)
  }

  // PDF向き選択後の実際のダウンロード処理
  const handlePdfOrientationSelect = async (orientation: PdfOrientation) => {
    if (!businessAbstract) return

    // 各スライドのrefを配列にまとめる
    const slideRefs = [
      businessAbstractSlideRef,
      threeCSlideRef,
      investmentPlanSlideRef,
      executionPlanSlideRef,
      faqSlideRef,
      partnersSlideRef,
    ]

    await downloadPdf(businessAbstract.projectName, slideRefs, orientation)
  }

  // 削除確認処理
  const handleDeleteConfirm = async () => {
    if (!id) return

    const success = await deleteBusinessIdea(id)
    if (success) {
      setIsDeleteModalOpen(false)
      navigate('/myidea')
    }
  }

  // 削除モーダルを閉じる処理
  const handleDeleteModalClose = () => {
    if (!isDeleting) {
      setIsDeleteModalOpen(false)
    }
  }

  // 編集モードのキャンセル
  const handleCancelEdit = () => {
    setIsEditing(false)

    // 事業概要フォームをリセット
    if (businessAbstract) {
      resetBusinessAbstract({
        projectName: businessAbstract.projectName,
        marketBackground: businessAbstract.sections.marketBackground,
        competitorAnalysis: businessAbstract.sections.competitorAnalysis,
        businessChance: businessAbstract.sections.businessChance,
        businessContentFirst: businessAbstract.sections.businessContentFirst,
        businessContentSecond: businessAbstract.sections.businessContentSecond,
      })
    }

    // 3C分析フォームをリセット
    if (threeC) {
      resetThreeC({
        needs: threeC.customer.needs,
        targetFirst: threeC.customer.targetFirst,
        targetSecond: threeC.customer.targetSecond,
        marketTrendFirst: threeC.customer.marketTrendFirst,
        marketTrendSecond: threeC.customer.marketTrendSecond,
        mainCompetitorFirst: threeC.competitor.mainCompetitorFirst,
        mainCompetitorSecond: threeC.competitor.mainCompetitorSecond,
        advantageFirst: threeC.competitor.advantageFirst,
        advantageSecond: threeC.competitor.advantageSecond,
        differentiationFirst: threeC.competitor.differentiationFirst,
        differentiationSecond: threeC.competitor.differentiationSecond,
        strengthFirst: threeC.company.strengthFirst,
        strengthSecond: threeC.company.strengthSecond,
        strengthThird: threeC.company.strengthThird,
        resourceFirst: threeC.company.resourceFirst,
        resourceSecond: threeC.company.resourceSecond,
        strategyFirst: threeC.company.strategyFirst,
        strategySecond: threeC.company.strategySecond,
        strategyThird: threeC.company.strategyThird,
      })
    }

    // 投資計画フォームをリセット
    if (investmentPlan) {
      try {
        resetInvestmentPlan({
          profitStructure: investmentPlan.profitStructure,
          profitStructureItems: JSON.parse(
            investmentPlan.profitStructureItems || '[]'
          ),
          cashFlow: investmentPlan.cashFlow,
          cashFlowItems: JSON.parse(investmentPlan.cashFlowItems || '[]'),
          financialPlan: investmentPlan.financialPlan,
          financialPlanItems: JSON.parse(
            investmentPlan.financialPlanItems || '[]'
          ),
        })
      } catch (e) {
        console.error('収益モデルと投資計画データのパースに失敗しました:', e)
        showSnackBar(
          ['収益モデルと投資計画の読み込みに失敗しました。'],
          'error'
        )
      }
    }

    // 実行計画&必要なリソースフォームをリセット
    if (slideExecutionPlan) {
      try {
        resetSlideExecutionPlan({
          resource: slideExecutionPlan.resource,
          maxPeriod: slideExecutionPlan.maxPeriod,
          scheduleItems: JSON.parse(slideExecutionPlan.scheduleItems || '[]'),
        })
      } catch (e) {
        console.error('実行計画&必要なリソースデータのパースに失敗しました:', e)
        showSnackBar(
          ['実行計画&必要なリソースの読み込みに失敗しました。'],
          'error'
        )
      }
    }

    // FAQ・懸念点まとめフォームをリセット
    if (slideFaq) {
      try {
        resetSlideFaq({
          concerns: JSON.parse(slideFaq.concerns || '[]'),
          faqs: JSON.parse(slideFaq.faqs || '[]'),
        })
      } catch (e) {
        console.error('FAQ・懸念点まとめデータのパースに失敗しました:', e)
        showSnackBar(['FAQ・懸念点まとめの読み込みに失敗しました。'], 'error')
      }
    }

    // オススメ共創パートナー企業フォームをリセット
    if (slidePartners) {
      try {
        resetSlidePartners({
          partners: JSON.parse(slidePartners.partners || '[]'),
        })
      } catch (e) {
        console.error(
          'オススメ共創パートナー企業データのパースに失敗しました:',
          e
        )
        showSnackBar(
          ['オススメ共創パートナー企業の読み込みに失敗しました。'],
          'error'
        )
      }
    }

    // AI指示フォームをリセット
    resetBusinessAbstractAi({ aiInstruction: '' })
    resetThreeCAi({ aiInstruction: '' })
    resetInvestmentPlanAi({ aiInstruction: '' })
    resetSlideExecutionPlanAi({ aiInstruction: '' })
    resetSlideFaqAi({ aiInstruction: '' })
    resetSlidePartnersAi({ aiInstruction: '' })
  }

  const handleAbstractFormSubmit = async (data: BusinessAbstractFormData) => {
    // 変更のあったフィールドのみを抽出
    const dirtyData: Partial<BusinessAbstractFormData> = {}
    Object.keys(businessAbstractFormState.dirtyFields).forEach((key) => {
      const typedKey = key as keyof BusinessAbstractFormData
      const value = data[typedKey]
      if (typedKey === 'thumbnailUrl') {
        dirtyData[typedKey] = value as string | null | undefined
      } else {
        dirtyData[typedKey] = value as string
      }
    })

    // 更新
    await updateBusinessAbstract(
      id || '',
      dirtyData as BusinessAbstractFormData,
      setBusinessAbstract,
      resetBusinessAbstract
    )
  }

  // 事業概要のAI再生成処理
  const handleAbstractAiRegenerate = async (data: AiInstructionFormData) => {
    if (!businessAbstract) return

    // フォームに変更がある場合は事前に更新
    if (businessAbstractFormState.isDirty) {
      const formData = {
        projectName: businessAbstract.projectName,
        thumbnailUrl: businessAbstract.thumbnailUrl,
        marketBackground: businessAbstract.sections.marketBackground,
        competitorAnalysis: businessAbstract.sections.competitorAnalysis,
        businessChance: businessAbstract.sections.businessChance,
        businessContentFirst: businessAbstract.sections.businessContentFirst,
        businessContentSecond: businessAbstract.sections.businessContentSecond,
      }

      // 更新
      await updateBusinessAbstract(
        id || '',
        formData,
        setBusinessAbstract,
        resetBusinessAbstract
      )
    }

    // AI再生成
    await regenerateBusinessAbstract(
      id || '',
      data.aiInstruction,
      setBusinessAbstract,
      resetBusinessAbstractAi,
      resetBusinessAbstract
    )
  }

  const handleThreeCFormSubmit = async (data: ThreeCFormData) => {
    // 変更のあったフィールドのみを抽出
    const dirtyData: Partial<ThreeCFormData> = {}
    Object.keys(threeCFormState.dirtyFields).forEach((key) => {
      const typedKey = key as keyof ThreeCFormData
      dirtyData[typedKey] = data[typedKey]
    })

    await updateThreeC(id || '', dirtyData as ThreeCFormData, setThreeC)
  }

  // 3C分析のAI再生成処理
  const handleThreeCAiRegenerate = async (data: AiInstructionFormData) => {
    if (!threeC) return

    // フォームに変更がある場合は事前に更新
    if (threeCFormState.isDirty) {
      const formData = {
        needs: threeC.customer.needs,
        targetFirst: threeC.customer.targetFirst,
        targetSecond: threeC.customer.targetSecond,
        marketTrendFirst: threeC.customer.marketTrendFirst,
        marketTrendSecond: threeC.customer.marketTrendSecond,
        mainCompetitorFirst: threeC.competitor.mainCompetitorFirst,
        mainCompetitorSecond: threeC.competitor.mainCompetitorSecond,
        advantageFirst: threeC.competitor.advantageFirst,
        advantageSecond: threeC.competitor.advantageSecond,
        differentiationFirst: threeC.competitor.differentiationFirst,
        differentiationSecond: threeC.competitor.differentiationSecond,
        strengthFirst: threeC.company.strengthFirst,
        strengthSecond: threeC.company.strengthSecond,
        strengthThird: threeC.company.strengthThird,
        resourceFirst: threeC.company.resourceFirst,
        resourceSecond: threeC.company.resourceSecond,
        strategyFirst: threeC.company.strategyFirst,
        strategySecond: threeC.company.strategySecond,
        strategyThird: threeC.company.strategyThird,
      }

      // 更新
      await updateThreeC(id || '', formData, setThreeC)
    }

    // AI再生成
    await regenerateThreeC(
      id || '',
      data.aiInstruction,
      setThreeC,
      resetThreeCAi,
      resetThreeC
    )
  }

  const handleInvestmentPlanFormSubmit = async (
    data: InvestmentPlanFormData
  ) => {
    // 配列データをJSONに変換
    const convertedData = {
      ...data,
      profitStructureItems: JSON.stringify(data.profitStructureItems),
      cashFlowItems: JSON.stringify(data.cashFlowItems),
      financialPlanItems: JSON.stringify(data.financialPlanItems),
    }

    // 変更のあったフィールドのみを抽出
    const dirtyData: InvestmentPlanApiData = {}
    Object.keys(investmentPlanFormState.dirtyFields).forEach((key) => {
      const typedKey = key as keyof typeof convertedData
      dirtyData[typedKey] = convertedData[typedKey]
    })

    await updateInvestmentPlan(
      id || '',
      dirtyData,
      setInvestmentPlan,
      resetInvestmentPlan
    )
  }

  // 投資計画のAI再生成処理
  const handleInvestmentPlanAiRegenerate = async (
    data: AiInstructionFormData
  ) => {
    if (!investmentPlan) return

    // フォームに変更がある場合は事前に更新
    if (investmentPlanFormState.isDirty) {
      const formData = {
        profitStructure: investmentPlan.profitStructure,
        profitStructureItems: investmentPlan.profitStructureItems,
        cashFlow: investmentPlan.cashFlow,
        cashFlowItems: investmentPlan.cashFlowItems,
        financialPlan: investmentPlan.financialPlan,
        financialPlanItems: investmentPlan.financialPlanItems,
      }

      // 更新
      await updateInvestmentPlan(
        id || '',
        formData,
        setInvestmentPlan,
        resetInvestmentPlan
      )
    }

    // AI再生成
    await regenerateInvestmentPlan(
      id || '',
      data.aiInstruction,
      setInvestmentPlan,
      resetInvestmentPlanAi,
      resetInvestmentPlan
    )
  }

  // 実行計画&必要なリソースフォーム送信
  const handleSlideExecutionPlanFormSubmit = async (
    data: ExecutionPlanFormData
  ) => {
    if (!slideExecutionPlan) return

    // 配列データをJSONに変換
    const convertedData = {
      resource: data.resource,
      maxPeriod: data.maxPeriod,
      scheduleItems: JSON.stringify(data.scheduleItems),
    }

    await updateExecutionPlan(
      id || '',
      convertedData,
      setSlideExecutionPlan,
      resetSlideExecutionPlan
    )
  }

  // 実行計画&必要なリソースAI再生成
  const handleSlideExecutionPlanAiRegenerate = async (
    data: AiInstructionFormData
  ) => {
    if (!slideExecutionPlan) return

    // フォームに変更がある場合は事前に更新
    if (slideExecutionPlanFormState.isDirty) {
      const formData = {
        resource: slideExecutionPlan.resource,
        maxPeriod: slideExecutionPlan.maxPeriod,
        scheduleItems: slideExecutionPlan.scheduleItems,
      }

      // 更新
      await updateExecutionPlan(
        id || '',
        formData,
        setSlideExecutionPlan,
        resetSlideExecutionPlan
      )
    }

    // AI再生成
    await regenerateExecutionPlan(
      id || '',
      data.aiInstruction,
      setSlideExecutionPlan,
      resetSlideExecutionPlanAi,
      resetSlideExecutionPlan
    )
  }

  // FAQ・懸念点まとめフォーム送信
  const handleSlideFaqFormSubmit = async (data: FaqFormData) => {
    // 変更のあったフィールドのみを抽出
    const convertedData = {
      concerns: JSON.stringify(data.concerns),
      faqs: JSON.stringify(data.faqs),
    }

    await updateFaq(id || '', convertedData, setSlideFaq, resetSlideFaq)
  }

  // オススメ共創パートナー企業フォーム送信
  const handleSlidePartnersFormSubmit = async (data: PartnersFormData) => {
    // 配列データをJSONに変換
    const convertedData = {
      partners: JSON.stringify(data.partners),
    }

    await updatePartners(
      id || '',
      convertedData,
      setSlidePartners,
      resetSlidePartners
    )
  }

  // FAQ・懸念点まとめAI再生成
  const handleSlideFaqAiRegenerate = async (data: AiInstructionFormData) => {
    if (!slideFaq) return

    // フォームに変更がある場合は事前に更新
    if (slideFaqFormState.isDirty) {
      const formData = {
        concerns: slideFaq.concerns,
        faqs: slideFaq.faqs,
      }

      // 更新
      await updateFaq(id || '', formData, setSlideFaq, resetSlideFaq)
    }

    // AI再生成
    await regenerateFaq(
      id || '',
      data.aiInstruction,
      setSlideFaq,
      resetSlideFaqAi,
      resetSlideFaq
    )
  }

  // オススメ共創パートナー企業AI再生成
  const handleSlidePartnersAiRegenerate = async (
    data: AiInstructionFormData
  ) => {
    if (!slidePartners) return

    // フォームに変更がある場合は事前に更新
    if (slidePartnersFormState.isDirty) {
      const formData = {
        partners: slidePartners.partners,
      }

      // 更新
      await updatePartners(
        id || '',
        formData,
        setSlidePartners,
        resetSlidePartners
      )
    }

    // AI再生成
    await regeneratePartners(
      id || '',
      data.aiInstruction,
      setSlidePartners,
      resetSlidePartnersAi,
      resetSlidePartners
    )
  }

  // 各種スライド情報取得中
  if (
    isBusinessAbstractLoading ||
    isThreeCLoading ||
    isInvestmentPlanLoading ||
    isSlideExecutionPlanLoading ||
    isSlideFaqLoading ||
    isSlidePartnersLoading
  ) {
    return (
      <AuthenticatedLayout>
        <div className="flex justify-center py-20">
          <LoadingSpinner />
        </div>
      </AuthenticatedLayout>
    )
  }

  if (!businessAbstract) {
    return (
      <AuthenticatedLayout>
        <div className="container mx-auto px-4 py-8">
          <div className="text-center py-20 text-main-darkgray">
            事業アイデアが見つかりません。
          </div>
        </div>
      </AuthenticatedLayout>
    )
  }

  return (
    <AuthenticatedLayout>
      <AiLoadingOverlay
        message="AI再生成中です..."
        description="生成には約1分程度かかります"
        isVisible={isRegenerating}
      />

      {isUpdating && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex justify-center py-20">
          <LoadingSpinner />
        </div>
      )}

      <Breadcrumb
        items={[
          { label: 'ホーム', path: '/dashboard' },
          { label: 'マイアイデア', path: '/myidea' },
          { label: '事業アイデア詳細' },
        ]}
      />

      <div className="mb-4 flex items-center">
        <div className="flex gap-4">
          {!isEditing ? (
            <>
              <FlatButton onClick={handleStartEdit} disabled={isDownloading}>
                編集する
              </FlatButton>
              <FlatButton
                onClick={handlePdfDownload}
                disabled={isDownloading}
                outline
                className="min-w-40"
              >
                {isDownloading ? (
                  <LoadingSpinner size={24} />
                ) : (
                  'PDFダウンロード'
                )}
              </FlatButton>
              <FlatButton
                onClick={handleDeleteClick}
                disabled={isDeleting || isDownloading}
                className="bg-main-red text-white"
              >
                削除する
              </FlatButton>
            </>
          ) : (
            <FlatButton
              outline
              onClick={handleCancelEdit}
              disabled={isUpdating || isRegenerating}
            >
              編集をやめる
            </FlatButton>
          )}
        </div>
        {/* 評価ボタン */}
        <div className="ml-6">
          <EvaluationButton
            currentEvaluation={currentEvaluation}
            onEvaluate={evaluate}
            disabled={isEvaluating}
          />
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={handleDeleteModalClose}
        onConfirm={handleDeleteConfirm}
        itemName={businessAbstract?.projectName || '事業アイデア'}
        isDeleting={isDeleting}
      />

      {/* PDF向き選択モーダル */}
      <PdfOrientationModal
        isOpen={isPdfModalOpen}
        onClose={() => setIsPdfModalOpen(false)}
        onSelectOrientation={handlePdfOrientationSelect}
      />

      {/* 事業概要スライド */}
      <BusinessAbstractSlide
        ref={businessAbstractSlideRef}
        businessAbstract={businessAbstract}
        isEditing={isEditing}
        isUpdating={isUpdating || isRegenerating}
        isDirty={businessAbstractFormState.isDirty}
        register={registerBusinessAbstract}
        handleSubmit={handleSubmitBusinessAbstract}
        setValue={setValueBusinessAbstract}
        control={controlBusinessAbstract}
        registerAi={registerBusinessAbstractAi}
        handleSubmitAi={handleSubmitBusinessAbstractAi}
        aiInstruction={businessAbstractAiInstruction}
        onFormSubmit={handleAbstractFormSubmit}
        onAiRegenerate={handleAbstractAiRegenerate}
      />
      {/* 3C分析スライド */}
      {threeC && (
        <ThreeCSlide
          ref={threeCSlideRef}
          threeC={threeC}
          isEditing={isEditing}
          isUpdating={isUpdating || isRegenerating}
          isDirty={threeCFormState.isDirty}
          register={registerThreeC}
          handleSubmit={handleSubmitThreeC}
          registerAi={registerThreeCAi}
          handleSubmitAi={handleSubmitThreeCAi}
          aiInstruction={threeCAiInstruction}
          onFormSubmit={handleThreeCFormSubmit}
          onAiRegenerate={handleThreeCAiRegenerate}
        />
      )}
      {/* 投資計画スライド */}
      {investmentPlan && (
        <InvestmentPlanSlide
          ref={investmentPlanSlideRef}
          investmentPlan={investmentPlan}
          isEditing={isEditing}
          isUpdating={isUpdating || isRegenerating}
          isDirty={investmentPlanFormState.isDirty}
          register={registerInvestmentPlan}
          control={controlInvestmentPlan}
          handleSubmit={handleSubmitInvestmentPlan}
          registerAi={registerInvestmentPlanAi}
          handleSubmitAi={handleSubmitInvestmentPlanAi}
          aiInstruction={investmentPlanAiInstruction}
          onFormSubmit={handleInvestmentPlanFormSubmit}
          onAiRegenerate={handleInvestmentPlanAiRegenerate}
        />
      )}
      {/* 実行計画&必要なリソーススライド */}
      {slideExecutionPlan && (
        <ExecutionPlanSlide
          ref={executionPlanSlideRef}
          slideExecutionPlan={slideExecutionPlan}
          isEditing={isEditing}
          isUpdating={isUpdating || isRegenerating}
          isDirty={slideExecutionPlanFormState.isDirty}
          register={registerSlideExecutionPlan}
          handleSubmit={handleSubmitSlideExecutionPlan}
          control={controlSlideExecutionPlan}
          setValue={setSlideExecutionPlanValue}
          registerAi={registerSlideExecutionPlanAi}
          handleSubmitAi={handleSubmitSlideExecutionPlanAi}
          aiInstruction={slideExecutionPlanAiInstruction}
          onFormSubmit={handleSlideExecutionPlanFormSubmit}
          onAiRegenerate={handleSlideExecutionPlanAiRegenerate}
        />
      )}
      {/* FAQ・懸念点まとめスライド */}
      {slideFaq && (
        <FaqSlide
          ref={faqSlideRef}
          slideFaq={slideFaq}
          isEditing={isEditing}
          isUpdating={isUpdating || isRegenerating}
          isDirty={slideFaqFormState.isDirty}
          register={registerSlideFaq}
          handleSubmit={handleSubmitSlideFaq}
          control={controlSlideFaq}
          registerAi={registerSlideFaqAi}
          handleSubmitAi={handleSubmitSlideFaqAi}
          aiInstruction={slideFaqAiInstruction}
          onFormSubmit={handleSlideFaqFormSubmit}
          onAiRegenerate={handleSlideFaqAiRegenerate}
        />
      )}
      {/* オススメ共創パートナー企業スライド */}
      {slidePartners && (
        <PartnersSlide
          ref={partnersSlideRef}
          slidePartners={slidePartners}
          isEditing={isEditing}
          isUpdating={isUpdating || isRegenerating}
          isDirty={slidePartnersFormState.isDirty}
          register={registerSlidePartners}
          handleSubmit={handleSubmitSlidePartners}
          control={controlSlidePartners}
          setValue={setValueSlidePartners}
          registerAi={registerSlidePartnersAi}
          handleSubmitAi={handleSubmitSlidePartnersAi}
          aiInstruction={slidePartnersAiInstruction}
          onFormSubmit={handleSlidePartnersFormSubmit}
          onAiRegenerate={handleSlidePartnersAiRegenerate}
        />
      )}
    </AuthenticatedLayout>
  )
}

export default BusinessDetailPage
